import { apiFetch } from "../utils/apiFetch";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { IoMdTime } from "react-icons/io";
import { IoCheckmark, IoCheckmarkDoneSharp } from "react-icons/io5";
import { BsThreeDotsVertical } from "react-icons/bs";
import { useAuth } from "../context/AuthContext";
import { FaUserPlus } from "react-icons/fa";
import { RxCross2 } from "react-icons/rx";
import ViewMembers from "./ViewMembers";
import GroupLeave from "./GroupLeave";
import { CiCirclePlus } from "react-icons/ci";
import MessageListShimmer from "./MessageListShimmer";
import { useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router";
import { FiArrowLeft } from "react-icons/fi";

const ChatSpace = ({
  socket,
  emitWithAck,
  activeChat,
  userId,
  setChats,
  listOfMessagesAndInfo,
  setListOfMessagesAndInfo,
  scrollToBottom,
  setScrollToBottom,
  fetchPeople,
  people,
  setPeople,
  setActiveChat,
  groupSettings,
  setGroupSettings,
  workspace,
  members,
  setMembers, //workspace members
  setUserGotRemovedPopUp,
  setWorkspace,
}) => {
  const client = useQueryClient();
  const { slug } = useParams();
  const [message, setMessage] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedFilePreviewUrl, setSelectedFilePreviewUrl] = useState(null);
  const [previewImageSrc, setPreviewImageSrc] = useState(null);
  const [isShort, setIsShort] = useState(false);
  const [canIaddIntObs, setCanIaddIntObs] = useState(false);
  const [updateStatusOfMessages, setUpdateStatusOfMessages] = useState(false);
  const [updateTheStatus, setUpdateTheStatus] = useState(null);
  const [toBeSent, setToBeSent] = useState([]);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isSocketConnected, setIsSocketConnected] = useState(
    Boolean(socket?.connected),
  );
  const listRef = useRef(null);
  const intersectionRef = useRef(null);
  const activeChatRef = useRef(activeChat);
  const isFetchingMoreRef = useRef(false);
  const hasMoreRef = useRef(false);
  const nextCursorRef = useRef(null);
  const loadMoreRef = useRef(async () => {});
  const retryQueueInFlightRef = useRef(false);
  const toBeSentRef = useRef([]);
  const offlineSeqRef = useRef(0);
  const { userInfo, userId: authenticatedUserId } = useAuth();
  const effectiveUserId = userId || authenticatedUserId;
  const [addMembersPopUp, setAddMembersPopUp] = useState(false);
  const [checkedValues, setCheckedValues] = useState([]);
  const [selectedPeopleToAddToGroup, setSelectedPeopleToAddToGroup] = useState(
    [],
  );
  const [addMembersSearch, setAddMembersSearch] = useState("");
  const [messageHoverInfo, setMessageHoverInfo] = useState(null);
  const [deleteMessagePopUp, setDeleteMessagePopUp] = useState(null);
  const [deleteMessageButtonSpinner, setDeleteMessageButtonSpinner] =
    useState(false);
  const [viewMembersPopUp, setViewMembersPopUp] = useState(false);
  const [groupLeavePopUp, setGroupLeavePopUp] = useState(false);
  const pushedChatIdRef = useRef(null);

  const getDateLabel = (dateString) => {
    const date = new Date(dateString); // "26 May 2026"

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const target = new Date(date);
    target.setHours(0, 0, 0, 0);

    const diffDays = Math.round((today - target) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "Yesterday";

    return dateString;
  };

  const filteredPeople = people?.filter((person) =>
    person.username
      .toLowerCase()
      .includes(addMembersSearch.toLocaleLowerCase()),
  );

  const mergeMessageWithLocalPayload = (incoming, existing) => {
    if (!existing) return incoming;
    if (incoming?.payload) return incoming;

    return {
      ...incoming,
      payload: existing?.payload || null,
      contentPreview:
        incoming?.contentPreview || existing?.contentPreview || "",
    };
  };

  const getMessageImageSrc = (mess) =>
    mess?.payload?.imageData || mess?.imageData || null;

  const getMessageText = (mess) =>
    mess?.payload?.content || mess?.contentPreview || mess?.content || "";

  const getMessageSenderId = (mess) =>
    String(mess?.sender?._id || mess?.sender || mess?.senderId || "");

  const getMessageReceiverId = (mess) =>
    mess?.receiver?.length === 1 ? String(mess?.receiver[0] || "") : null;

  const getTempMessageTimestamp = (tempId) => {
    const value = String(tempId || "");
    const parts = value.split("-");
    const ts = Number(parts?.[1] || 0);
    return Number.isFinite(ts) ? ts : 0;
  };

  const getMessageType = (file) => {
    if (!file) return "text";

    if (file.type.startsWith("image/")) return "image";
    if (file.type.startsWith("video/")) return "video";
    if (file.type.startsWith("audio/")) return "audio";

    return "file";
  };

  const onSelectFile = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setSelectedFile(file);

    if (
      file.type.startsWith("image/") ||
      file.type.startsWith("video/") ||
      file.type.startsWith("audio/")
    ) {
      setSelectedFilePreviewUrl(URL.createObjectURL(file));
    } else {
      setSelectedFilePreviewUrl(null);
    }
  };

  useEffect(() => {
    if (!updateTheStatus) return;
    if (!socket) return;

    const update = async () => {
      const targetId =
        updateTheStatus?.friend?._id || updateTheStatus?.group?._id;
      if (
        activeChatRef.current &&
        String(activeChatRef.current._id) === String(targetId)
      ) {
        try {
          await emitWithAck("message:viewed", {
            messageId: updateTheStatus.lastMessage._id,
          });
        } catch (err) {
          alert(err.message);
        }
      } else {
        try {
          await emitWithAck("message:delivered", {
            messageId: updateTheStatus.lastMessage._id,
          });
        } catch (err) {
          alert(err.message);
        }
      }
    };
    update();
  }, [updateTheStatus, socket, emitWithAck]);

  useEffect(() => {
    activeChatRef.current = activeChat;
    setCanIaddIntObs(false);
    hasMoreRef.current = false;
    nextCursorRef.current = null;
    isFetchingMoreRef.current = false;
  }, [activeChat?._id]);

  useEffect(() => {
    hasMoreRef.current = Boolean(listOfMessagesAndInfo?.pagination?.hasMore);
    nextCursorRef.current =
      listOfMessagesAndInfo?.pagination?.nextCursor || null;
  }, [listOfMessagesAndInfo]);

  useEffect(() => {
    toBeSentRef.current = Array.isArray(toBeSent) ? toBeSent : [];
  }, [toBeSent]);

  useEffect(() => {
    if (!socket) return;

    const onNew = async (friendInfo) => {
      // update chat list/messages state
      setChats((prev) => {
        const list = Array.isArray(prev) ? prev : [];
        const incomingFriendId = String(
          friendInfo?.friend?._id || friendInfo?.group?._id || "",
        );
        const existing = list.find(
          (fr) =>
            String(fr?.friend?._id || fr?.group?._id || "") ===
            incomingFriendId,
        );
        const rest = list.filter(
          (fr) =>
            String(fr?.friend?._id || fr?.group?._id || "") !==
            incomingFriendId,
        );
        const isActive =
          activeChatRef.current &&
          String(activeChatRef.current._id) === incomingFriendId;
        const nextUnreadCount = isActive
          ? 0
          : Number(existing?.unreadCount || 0) + 1;

        return [{ ...friendInfo, unreadCount: nextUnreadCount }, ...rest];
      });
      const targetId = friendInfo?.friend?._id || friendInfo?.group?._id;
      if (
        activeChatRef.current &&
        String(activeChatRef.current._id) === String(targetId)
      ) {
        const currentChatId = String(activeChatRef.current._id);
        setListOfMessagesAndInfo((prev) => {
          if (!prev) return prev;
          if (String(prev.chatId || "") !== currentChatId) return prev;
          const prevMessages = Array.isArray(prev.messages)
            ? prev.messages
            : [];

          const exists = prevMessages.some(
            (m) => String(m._id) === String(friendInfo.lastMessage._id),
          );
          if (exists) return prev;

          const nextState = {
            ...prev,
            messages: [...prevMessages, friendInfo.lastMessage],
          };

          return nextState;
        });
      }

      setUpdateTheStatus(friendInfo);
      // console.log("message:new", msg);
    };

    const onViewed = ({ messageId, statusOfMessage }) => {
      const normalizedMessageId = String(messageId);
      const currentChatId = String(activeChatRef.current?._id || "");

      setChats((prev) => {
        if (!Array.isArray(prev)) return prev;

        return prev.map((friendInfo) => {
          return String(friendInfo?.lastMessage?._id) === normalizedMessageId
            ? {
                ...friendInfo,
                lastMessage: {
                  ...friendInfo.lastMessage,
                  status: statusOfMessage,
                },
              }
            : friendInfo;
        });
      });

      // mark message viewed in state
      setListOfMessagesAndInfo((prev) => {
        if (!prev) return prev;
        if (String(prev.chatId || "") !== currentChatId) return prev;
        const prevMessages = Array.isArray(prev.messages) ? prev.messages : [];

        const nextState = {
          ...prev,
          messages: prevMessages.map((message) =>
            String(message._id) === normalizedMessageId
              ? { ...message, status: statusOfMessage }
              : message,
          ),
        };

        return nextState;
      });
    };

    const onGroupCreation = async ({
      groupId,
      pairKey,
      groupName,
      participants,
      unreadCount,
      createdBy,
      lastMessage,
      allParticipants,
    }) => {
      setChats((prev) => {
        return [
          {
            type: "group",
            group: {
              _id: groupId,
              name: groupName,
              participants,
              allParticipants,
            },
            unreadCount,
            lastMessage,
          },
          ...prev,
        ];
      });
      try {
        await emitWithAck("message:delivered", {
          messageId: lastMessage._id,
        });
      } catch (err) {
        alert(err.message);
      }
    };

    const onMembersAddition = async ({
      groupId,
      participants,
      // unreadCount,
      lastMessage,
      groupName,
      allParticipants,
      admin,
    }) => {
      setChats((prev) => {
        const isExisting = prev.some(
          (friendInfo) =>
            friendInfo?.group &&
            String(friendInfo.group._id) === String(groupId),
        );
        if (isExisting) {
          const target = prev.find(
            (friendInfo) => friendInfo?.group?._id === groupId,
          );
          return [
            {
              ...target,
              group: {
                ...target.group,
                participants,
                allParticipants,
                admin,
              },
              lastMessage,
              unreadCount:
                activeChatRef.current?._id === groupId
                  ? 0
                  : target.unreadCount + 1,
            },
            ...prev.filter((friendInfo) => {
              if (friendInfo?.friend) return true;
              else return friendInfo.group._id !== target.group._id;
            }),
          ];
        }
        return [
          {
            type: "group",
            group: {
              _id: groupId,
              name: groupName,
              participants,
              allParticipants,
              admin,
            },
            lastMessage,
            unreadCount: 1,
          },
          ...prev,
        ];
      });

      if (
        activeChatRef.current &&
        String(activeChatRef.current._id) === String(groupId)
      ) {
        setListOfMessagesAndInfo((prev) => {
          return { ...prev, messages: [...prev.messages, lastMessage] };
        });
        setActiveChat((prev) => {
          return {
            ...prev,
            email: participants
              .map((participant) => participant.username)
              .join(","),
            participants: participants,
            participantIds: participants.map((participant) => participant._id),
            allParticipants,
            admin,
          };
        });
        try {
          await emitWithAck("message:viewed", {
            messageId: lastMessage._id,
          });
        } catch (err) {
          alert(err.message);
        }
      } else {
        try {
          await emitWithAck("message:delivered", {
            messageId: lastMessage._id,
          });
        } catch (err) {
          alert(err.message);
        }
      }
    };

    const onMessageDeletion = async ({ messageId, conversationId }) => {
      setChats((prev) => {
        if (!prev) return prev;
        return prev.map((friendInfo) => {
          if (String(friendInfo.lastMessage._id) === String(messageId)) {
            return {
              ...friendInfo,
              lastMessage: {
                ...friendInfo.lastMessage,
                contentPreview: null,
                deleteStatus: "deleted",
              },
            };
          }
          return friendInfo;
        });
      });
      if (
        activeChatRef.current &&
        String(activeChatRef.current._id) === String(conversationId)
      ) {
        setListOfMessagesAndInfo((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            messages: prev.messages.map((mess) => {
              if (String(mess._id) === messageId) {
                return {
                  ...mess,
                  contentPreview: null,
                  deleteStatus: "deleted",
                };
              }
              return mess;
            }),
          };
        });
      }
    };

    const onMemberRemoval = async ({
      groupId,
      participants,
      removedId,
      admin,
      allParticipants,
    }) => {
      setChats((prev) => {
        if (!prev) return prev;
        if (String(removedId) !== String(userId)) {
          return prev.map((friendInfo) => {
            if (friendInfo?.group?._id === groupId) {
              return {
                ...friendInfo,
                group: {
                  ...friendInfo.group,
                  participants: participants,
                  admin: admin,
                  allParticipants,
                },
              };
            }
            return friendInfo;
          });
        }

        return prev.filter((friendInfo) => friendInfo?.group?._id !== groupId);
      });

      if (
        activeChatRef.current &&
        String(activeChatRef.current._id) === String(groupId)
      ) {
        setActiveChat((prev) => {
          if (String(removedId) !== String(userId)) {
            return {
              ...prev,
              email: participants
                .map((participant) => participant.username)
                .join(","),
              participants: participants,
              participantIds: participants.map(
                (participant) => participant._id,
              ),
              admin: admin,
              allParticipants,
            };
          }

          return null;
        });
      }
    };

    const onMemberStatusChange = async ({ groupId, admin }) => {
      try {
        setChats((prev) => {
          if (!prev) return prev;
          return prev.map((friendInfo) => {
            if (friendInfo?.group?._id === groupId) {
              return {
                ...friendInfo,
                group: {
                  ...friendInfo.group,
                  admin: admin,
                },
              };
            }
            return friendInfo;
          });
        });

        if (
          activeChatRef.current &&
          String(activeChatRef.current._id) === String(groupId)
        ) {
          setActiveChat((prev) => {
            return {
              ...prev,
              admin: admin,
            };
          });
        }
      } catch (err) {
        console.log(err.message);
      }
    };

    const onMemberLeaving = async ({
      groupId,
      participants,
      admin,
      allParticipants,
    }) => {
      setChats((prev) => {
        if (!prev) return prev;
        return prev.map((friendInfo) => {
          if (friendInfo?.group?._id === groupId) {
            return {
              ...friendInfo,
              group: {
                ...friendInfo.group,
                participants: participants,
                admin: admin,
                allParticipants,
              },
            };
          }
          return friendInfo;
        });
      });

      if (
        activeChatRef.current &&
        String(activeChatRef.current._id) === String(groupId)
      ) {
        setActiveChat((prev) => {
          return {
            ...prev,
            email: participants
              .map((participant) => participant.username)
              .join(","),
            participants: participants,
            participantIds: participants.map((participant) => participant._id),
            admin: admin,
            allParticipants,
          };
        });
      }
    };

    const onRoleChange = ({ modifieeId, modifiedRole }) => {
      setMembers((prev) => {
        return prev.map((member) => {
          if (String(member.userId._id) === String(modifieeId)) {
            return {
              ...member,
              role: modifiedRole,
            };
          }
          return member;
        });
      });

      setWorkspace((prev) => {
        return {
          ...prev,
          members: prev.members.map((member) => {
            if (String(member.userId) === String(modifieeId)) {
              return {
                ...member,
                role: modifiedRole,
              };
            }
            return member;
          }),
        };
      });
    };

    const onWorkspaceMemberRemoval = ({ removedId }) => {
      if (String(removedId) === String(userId)) {
        setUserGotRemovedPopUp(true);
      } else {
        setMembers((prev) =>
          prev.filter(
            (memberInfo) => String(memberInfo.userId._id) !== String(removedId),
          ),
        );

        setWorkspace((prev) => {
          return {
            ...prev,
            members: prev.members.filter(
              (member) => String(member.userId) !== String(removedId),
            ),
          };
        });
      }
    };

    const onWorkspaceMemberExit = ({ leftMemberId }) => {
      setMembers((prev) =>
        prev?.filter(
          (memberInfo) =>
            String(memberInfo.userId._id) !== String(leftMemberId),
        ),
      );

      setWorkspace((prev) => {
        return {
          ...prev,
          members: prev.members.filter(
            (member) => String(member.userId) !== String(leftMemberId),
          ),
        };
      });
    };

    const onWorkspaceMemberJoin = ({
      workspaceMembers,
      detailedWorkspaceMembers,
    }) => {
      setMembers((prev) => detailedWorkspaceMembers);

      setWorkspace((prev) => {
        return { ...prev, members: workspaceMembers };
      });
    };

    socket.on("message:new", onNew);
    socket.on("message:viewed", onViewed);
    socket.on("group:created", onGroupCreation);
    socket.on("group:membersAdded", onMembersAddition);
    socket.on("group:memberRemoved", onMemberRemoval);
    socket.on("group:memberStatusChanged", onMemberStatusChange);
    socket.on("group:memberLeft", onMemberLeaving);
    socket.on("message:deleted", onMessageDeletion);
    socket.on("workspace:roleChanged", onRoleChange);
    socket.on("workspace:memberRemoved", onWorkspaceMemberRemoval);
    socket.on("workspace:memberLeft", onWorkspaceMemberExit);
    socket.on("workspace:memberJoin", onWorkspaceMemberJoin);

    return () => {
      socket.off("message:new", onNew);
      socket.off("message:viewed", onViewed);
      socket.off("group:created", onGroupCreation);
      socket.off("group:membersAdded", onMembersAddition);
      socket.off("group:memberRemoved", onMemberRemoval);
      socket.off("group:memberStatusChanged", onMemberStatusChange);
      socket.off("group:memberLeft", onMemberLeaving);
      socket.off("message:deleted", onMessageDeletion);
      socket.off("workspace:roleChanged", onRoleChange);
      socket.off("workspace:memberRemoved", onWorkspaceMemberRemoval);
      socket.off("workspace:memberLeft", onWorkspaceMemberExit);
      socket.off("workspace:memberJoin", onWorkspaceMemberJoin);
    };
  }, [socket]);

  // useEffect(() => {
  //   if (!activeChat?._id || !effectiveUserId) return;
  //   setListOfMessagesAndInfo(null);
  // }, [activeChat, effectiveUserId]);

  useEffect(() => {
    if (!socket || !isSocketConnected || !activeChat?._id) return;
    let cancelled = false;
    const requestedChatId = String(activeChat._id);

    const fetchData = async () => {
      try {
        const cacheKey = ["account", effectiveUserId, "dm-messages", slug, requestedChatId];
        await client.fetchQuery({ queryKey: cacheKey, queryFn: async () => {
          const incoming = await emitWithAck("messages:list", { userId: activeChat._id, type: activeChat.type });
          const previous = client.getQueryData(cacheKey);
          const ids = new Set(incoming.messages.map(item => String(item._id)));
          const older = (previous?.messages || []).filter(item => !ids.has(String(item._id)) && !["not_sent", "failed"].includes(item.status));
          const previousById = new Map((previous?.messages || []).map(item => [String(item._id), item]));
          const messages = [...older, ...incoming.messages.map(item => mergeMessageWithLocalPayload(item, previousById.get(String(item._id))))]
            .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt) || String(a._id).localeCompare(String(b._id)));
          return { ...incoming, chatId: requestedChatId, messages,
            pagination: older.length ? previous.pagination : incoming.pagination };
        } });
        if (cancelled) return;
        if (String(activeChatRef.current?._id || "") !== requestedChatId)
          return;
        setCanIaddIntObs(true);
        setScrollToBottom((prev) => !prev);
        setUpdateStatusOfMessages((prev) => !prev);
      } catch (err) {
        alert(err.message);
      }
    };

    fetchData();

    return () => {
      cancelled = true;
    };
  }, [activeChat?._id, socket, emitWithAck, workspace?._id, isSocketConnected, client, effectiveUserId, slug]);

  useEffect(() => {
    if (!canIaddIntObs) return;
    if (!listRef.current || !intersectionRef.current) return;

    const loadMore = async () => {
      if (isFetchingMoreRef.current) return;
      if (!hasMoreRef.current || !nextCursorRef.current) return;
      if (!activeChatRef.current?._id) return;
      const requestedChatId = String(activeChatRef.current._id);

      isFetchingMoreRef.current = true;
      try {
        const data = await emitWithAck("messages:list", {
          userId: activeChatRef.current._id,
          type: activeChatRef.current.type,
          cursor: nextCursorRef.current,
        });
        const safeIncomingMessages = Array.isArray(data?.messages)
          ? data.messages
          : [];

        setListOfMessagesAndInfo((prev) => {
          if (!prev) return prev;
          if (String(prev.chatId || "") !== requestedChatId) return prev;
          const existingMessages = Array.isArray(prev.messages)
            ? prev.messages
            : [];
          const existingById = new Map(
            existingMessages.map((m) => [String(m?._id), m]),
          );
          const existingIds = new Set(
            existingMessages.map((m) => String(m?._id)),
          );
          const incomingMessages = safeIncomingMessages
            .filter((m) => !existingIds.has(String(m?._id)))
            .map((msg) =>
              mergeMessageWithLocalPayload(
                msg,
                existingById.get(String(msg?._id)),
              ),
            );

          const nextState = {
            ...prev,
            messages: [...incomingMessages, ...existingMessages],
            pagination: data?.pagination || prev.pagination,
          };

          return nextState;
        });
      } catch (err) {
        alert(err.message);
      } finally {
        isFetchingMoreRef.current = false;
      }
    };

    loadMoreRef.current = loadMore;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry?.isIntersecting) return;
        loadMore();
      },
      {
        root: listRef.current,
        threshold: 0,
        rootMargin: "120px 0px 0px 0px",
      },
    );

    observer.observe(intersectionRef.current);

    return () => observer.disconnect();
  }, [canIaddIntObs, emitWithAck]);

  useEffect(() => {
    if (!canIaddIntObs || !listRef.current) return;

    const el = listRef.current;
    const onScroll = () => {
      // Fallback for missed IO events: if user is near top, try loading more.
      if (el.scrollTop <= 120) {
        loadMoreRef.current();
      }
    };

    el.addEventListener("scroll", onScroll);
    return () => el.removeEventListener("scroll", onScroll);
  }, [canIaddIntObs]);

  useEffect(() => {
    if (!canIaddIntObs || !listRef.current || !intersectionRef.current) return;

    const rootRect = listRef.current.getBoundingClientRect();
    const targetRect = intersectionRef.current.getBoundingClientRect();
    const nearTop = targetRect.top <= rootRect.top + 120;

    // If sentinel is already near top, IO may not emit again; force a try.
    if (nearTop) {
      loadMoreRef.current();
    }
  }, [
    canIaddIntObs,
    listOfMessagesAndInfo?.pagination?.nextCursor,
    listOfMessagesAndInfo?.pagination?.hasMore,
  ]);

  useLayoutEffect(() => {
    if (!canIaddIntObs) return;
    setIsShort(listRef.current.scrollHeight <= listRef.current.clientHeight);
    listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [canIaddIntObs, scrollToBottom]);

  useEffect(() => {
    if (!canIaddIntObs) return;
    if (!socket) return;
    if (!listOfMessagesAndInfo) return;
    if (
      String(listOfMessagesAndInfo.chatId || "") !==
      String(activeChat?._id || "")
    )
      return;

    const updateStatuses = async () => {
      const lastMessage =
        listOfMessagesAndInfo.messages[
          listOfMessagesAndInfo.messages.length - 1
        ];
      if (
        lastMessage &&
        lastMessage.receiver.find(
          (rec) => String(rec) === String(effectiveUserId),
        ) &&
        (lastMessage.status === "delivered" || lastMessage.status === "sent")
      ) {
        try {
          await emitWithAck("message:viewed", { messageId: lastMessage._id });
        } catch (err) {
          alert(err.message);
        }
      }
    };

    updateStatuses();
  }, [
    updateStatusOfMessages,
    socket,
    emitWithAck,
    listOfMessagesAndInfo,
    effectiveUserId,
  ]);

  const sendMessage = async (tempId, payload) => {
    if (!socket || !socket.connected || !navigator.onLine) {
      setToBeSent((prev) => {
        const exists = prev.some(
          (item) => String(item?.tempId || "") === String(tempId),
        );
        if (exists) return prev;
        offlineSeqRef.current += 1;
        return [...prev, { tempId, payload, seq: offlineSeqRef.current }];
      });
      return;
    }

    try {
      payload = { ...payload, attachment: {} };
      if (selectedFile && payload.messageType !== "text") {
        const sigRes = await apiFetch(
          `${process.env.API_URL}/api/cloudinary/filesignature?workspaceId=${encodeURIComponent(workspace._id)}`,
          {
            },
        );

        const sig = await sigRes.json();
        if (!sigRes.ok) throw new Error(sig.message || "Upload access denied");

        const formData = new FormData();
        formData.append("file", selectedFile);
        formData.append("api_key", sig.apiKey);
        formData.append("timestamp", sig.timestamp);
        formData.append("signature", sig.signature);
        formData.append("folder", sig.folder);

        const resourceType = selectedFile.type.startsWith("image/")
          ? "image"
          : selectedFile.type.startsWith("video/") ||
              selectedFile.type.startsWith("audio/")
            ? "video"
            : "raw";

        const uploadRes = await apiFetch(
          `https://api.cloudinary.com/v1_1/${sig.cloudName}/${resourceType}/upload`,
          {
            method: "POST",
            body: formData,
          },
        );

        if (!uploadRes.ok) {
          throw new Error(uploadRes.status + ": " + uploadRes.statusText);
        }
        const cloudinaryData = await uploadRes.json();
        console.log("cloudinarydata", cloudinaryData);

        payload = {
          ...payload,
          attachment: {
            url: cloudinaryData.secure_url,
            publicId: cloudinaryData.public_id,
            name: selectedFile.name,
            mime: selectedFile.type,
            size: selectedFile.size,
            resourceType: cloudinaryData.resource_type,
            duration: cloudinaryData.duration || null,
            width: cloudinaryData.width || null,
            height: cloudinaryData.height || null,
          },
        };
      }
      const data = await emitWithAck("message:send", payload);
      const receiverId = String(payload?.receiverId || "");

      setListOfMessagesAndInfo((prev) => {
        if (!prev) return prev;
        if (String(prev.chatId || "") !== String(payload?.receiverId || "")) {
          return prev;
        }
        const prevMessages = Array.isArray(prev.messages) ? prev.messages : [];
        const exists = prevMessages.some(
          (m) => String(m._id) === String(data.message?._id),
        );
        const tempIndex = prevMessages.findIndex(
          (m) => String(m?._id || "") === String(tempId),
        );
        let nextMessages = prevMessages;
        if (exists) {
          nextMessages = prevMessages.filter(
            (message) => String(message?._id || "") !== String(tempId),
          );
        } else if (tempIndex >= 0) {
          const tempRow = prevMessages[tempIndex] || null;
          const merged = tempRow?.createdAt
            ? { ...data.message, createdAt: tempRow.createdAt }
            : data.message;
          nextMessages = prevMessages.map((m, idx) =>
            idx === tempIndex ? merged : m,
          );
        } else {
          nextMessages = [...prevMessages, data.message];
        }

        const nextState = {
          ...prev,
          messages: nextMessages,
        };

        return nextState;
      });

      setChats((prev) => {
        if (!Array.isArray(prev)) return prev;

        const activeId = receiverId;
        const target = prev.find(
          (friendInfo) =>
            String(friendInfo?.friend?._id || friendInfo?.group?._id || "") ===
            activeId,
        );
        if (!target) return prev;

        const updatedFriend = { ...target, lastMessage: data.message };
        const rest = prev.filter(
          (friendInfo) =>
            String(friendInfo?.friend?._id || friendInfo?.group?._id || "") !==
            activeId,
        );

        return [updatedFriend, ...rest];
      });
      setToBeSent((prev) =>
        prev.filter(
          (pending) => String(pending?.tempId || "") !== String(tempId),
        ),
      );
    } catch (err) {
      const errMsg = String(err?.message || "");
      if (!errMsg.includes("Socket not connected")) {
        alert(err.message);
      }
      // Keep optimistic row visible as failed and persist preview immediately.
      setChats((prev) => {
        if (!Array.isArray(prev)) return prev;
        const activeId = String(activeChat?._id || "");
        const next = prev.map((friendInfo) => {
          if (String(friendInfo?.friend?._id || "") !== activeId)
            return friendInfo;
          const failedLast = friendInfo?.lastMessage
            ? { ...friendInfo.lastMessage, status: "failed" }
            : friendInfo?.lastMessage;
          return { ...friendInfo, lastMessage: failedLast };
        });
        return next;
      });
      setToBeSent((prev) => {
        const exists = prev.some(
          (item) => String(item?.tempId || "") === String(tempId),
        );
        if (exists) return prev;
        offlineSeqRef.current += 1;
        return [...prev, { tempId, payload, seq: offlineSeqRef.current }];
      });
    }
  };

  useEffect(() => {
    const onOnline = () => setIsOnline(true);
    const onOffline = () => setIsOnline(false);

    setIsOnline(navigator.onLine);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);

    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, []);

  useEffect(() => {
    if (!socket) return;
    const onConnect = () => setIsSocketConnected(true);
    const onDisconnect = () => setIsSocketConnected(false);

    setIsSocketConnected(Boolean(socket.connected));
    socket.on("connect", onConnect);
    socket.on("disconnect", onDisconnect);

    return () => {
      socket.off("connect", onConnect);
      socket.off("disconnect", onDisconnect);
    };
  }, [socket, effectiveUserId]);

  useEffect(() => {
    if (!isOnline || !isSocketConnected || toBeSent.length === 0) return;
    if (retryQueueInFlightRef.current) return;

    const pending = [...toBeSent].sort((a, b) => {
      const seqA = Number(a?.seq || 0);
      const seqB = Number(b?.seq || 0);
      if (seqA !== seqB) return seqA - seqB;
      return (
        getTempMessageTimestamp(a?.tempId) - getTempMessageTimestamp(b?.tempId)
      );
    });
    retryQueueInFlightRef.current = true;

    const resendQueued = async () => {
      try {
        for (const item of pending) {
          const tempId = String(item?.tempId || "");
          const payload = item?.payload || null;
          if (!tempId || !payload) {
            setToBeSent((prev) =>
              prev.filter((q) => String(q?.tempId || "") !== tempId),
            );
            continue;
          }

          try {
            const data = await emitWithAck("message:send", payload);
            const receiverId = String(payload?.receiverId || "");

            setListOfMessagesAndInfo((prev) => {
              if (!prev) return prev;
              if (
                String(prev.chatId || "") !== String(payload?.receiverId || "")
              ) {
                return prev;
              }

              const prevMessages = Array.isArray(prev.messages)
                ? prev.messages
                : [];
              const exists = prevMessages.some(
                (m) => String(m?._id || "") === String(data.message?._id || ""),
              );
              const tempIndex = prevMessages.findIndex(
                (m) => String(m?._id || "") === tempId,
              );

              if (exists) {
                return {
                  ...prev,
                  messages: prevMessages.filter(
                    (m) => String(m?._id || "") !== tempId,
                  ),
                };
              }

              if (tempIndex >= 0) {
                const tempRow = prevMessages[tempIndex] || null;
                const merged = tempRow?.createdAt
                  ? { ...data.message, createdAt: tempRow.createdAt }
                  : data.message;
                return {
                  ...prev,
                  messages: prevMessages.map((m, idx) =>
                    idx === tempIndex ? merged : m,
                  ),
                };
              }

              return { ...prev, messages: [...prevMessages, data.message] };
            });

            setChats((prev) => {
              if (!Array.isArray(prev)) return prev;
              const target = prev.find(
                (f) => String(f?.friend?._id || "") === receiverId,
              );
              if (!target) return prev;
              const updated = { ...target, lastMessage: data.message };
              const rest = prev.filter(
                (f) => String(f?.friend?._id || "") !== receiverId,
              );
              return [updated, ...rest];
            });

            setToBeSent((prev) =>
              prev.filter((q) => String(q?.tempId || "") !== tempId),
            );
          } catch {
            // Preserve order: if one fails, stop and retry remaining later.
            break;
          }
        }
      } finally {
        retryQueueInFlightRef.current = false;
      }
    };

    resendQueued();
  }, [isOnline, isSocketConnected, toBeSent, emitWithAck]);

  useEffect(() => {
    const handlePopState = () => {
      if (window.innerWidth < 768 && activeChatRef.current?._id) {
        setActiveChat(null);
      }
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [setActiveChat]);

  useEffect(() => {
    const chatId = String(activeChat?._id || "");

    if (window.innerWidth >= 768 || !chatId) {
      pushedChatIdRef.current = null;
      return;
    }

    if (pushedChatIdRef.current === chatId) return;

    window.history.pushState(
      { mobileChatOpen: true, chatId },
      "",
      window.location.href,
    );
    pushedChatIdRef.current = chatId;
  }, [activeChat?._id]);

  const addMembersToGroup = async () => {
    try {
      if (selectedPeopleToAddToGroup.length === 0) {
        alert("cannot add zero members");
        return;
      }
      const data = await emitWithAck("group:addMembers", {
        memberIds: checkedValues,
        groupId: activeChat._id,
      });
      setChats((prev) => {
        const target = prev.find(
          (friendInfo) =>
            (friendInfo?.friend?._id || friendInfo?.group?._id) ===
            data.group._id,
        );
        if (!target) return prev;
        const modifiedTarget = {
          ...target,
          group: {
            ...target.group,
            allParticipants: data.group.allParticipants,
          },
          lastMessage: data.lastMessage,
        };
        const rest = prev.filter(
          (friendInfo) =>
            (friendInfo?.friend?._id || friendInfo?.group?._id) !==
            data.group._id,
        );
        return [modifiedTarget, ...rest];
      });
      setListOfMessagesAndInfo((prev) => {
        return { ...prev, messages: [...prev.messages, data.lastMessage] };
      });
      setActiveChat((prev) => {
        return {
          ...prev,
          email: data.group.participants
            .map((participant) => participant.username)
            .join(","),
          participants: data.group.participants,
          participantIds: data.group.participants.map(
            (participant) => participant._id,
          ),
          allParticipants: data.group.allParticipants,
        };
      });
      setAddMembersPopUp(false);
      try {
        await emitWithAck("message:viewed", {
          messageId: data.lastMessage._id,
        });
      } catch (err) {
        alert(err.message);
      }
    } catch (err) {
      alert(err.message);
    }
  };

  const deleteMessage = async (messageId, type) => {
    try {
      const data = await emitWithAck("message:delete", {
        messageId,
        type,
      });
      setDeleteMessageButtonSpinner(false);
      setDeleteMessagePopUp(null);
      setChats((prev) => {
        if (!prev) return prev;
        return prev.map((friendInfo) => {
          if (String(friendInfo.lastMessage._id) === String(data.messageId)) {
            return {
              ...friendInfo,
              lastMessage: {
                ...friendInfo.lastMessage,
                contentPreview: null,
                deleteStatus: data.deleteStatus,
              },
            };
          }
          return friendInfo;
        });
      });

      setListOfMessagesAndInfo((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          messages: prev.messages.map((mess) => {
            if (String(mess._id) === String(data.messageId)) {
              return {
                ...mess,
                contentPreview: null,
                deleteStatus: data.deleteStatus,
              };
            }
            return mess;
          }),
        };
      });
    } catch (err) {
      alert(err.message);
    }
  };

  console.log("activeChat", activeChat);
  console.log("listOfMessages", listOfMessagesAndInfo);
  console.log("people", people);

  return activeChat ? (
    !listOfMessagesAndInfo ? (
      // <h1>Loading...</h1>
      <MessageListShimmer />
    ) : (
      <div className="flex h-full min-h-0 w-full flex-1 flex-col md:min-w-0">
        <div className="flex min-h-0 flex-1 flex-col">
          <div
            className={`shrink-0 pb-2 border-b border-gray-200 p-2 pr-5 bg-white z-20 flex justify-between items-center`}
          >
            <div>
              <div className="flex items-center gap-2">
                <button type="button" title="All conversations" aria-label="All conversations" onClick={() => setActiveChat(null)} className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md hover:bg-gray-100 md:hidden"><FiArrowLeft /></button>
                {activeChat.type === "group" ? (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="lucide lucide-hash h-5 w-5 text-gray-600"
                  >
                    <line x1="4" x2="20" y1="9" y2="9"></line>
                    <line x1="4" x2="20" y1="15" y2="15"></line>
                    <line x1="10" x2="8" y1="3" y2="21"></line>
                    <line x1="16" x2="14" y1="3" y2="21"></line>
                  </svg>
                ) : (
                  <span className="relative flex size-10 shrink-0 overflow-hidden rounded-full h-6 w-6">
                    <img
                      className="aspect-square size-full"
                      src="https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQhxtx5uOH2FwWtC_-HAnN75PQqBFgfLF9D2w&s"
                    ></img>
                  </span>
                )}
                <h2 className="font-semibold text-gray-900">
                  {activeChat?.username}
                </h2>
              </div>
              {/* <div className="text-gray-600">
                {activeChat.email.length < 20
                  ? activeChat.email
                  : activeChat.email.substring(0, 20) + "..."}
              </div> */}
            </div>
            <div className="flex gap-1">
              {activeChat.type === "group" ? (
                <div
                  className="rounded-full hover:bg-gray-200 p-3"
                  onClick={() => {
                    fetchPeople();
                    setAddMembersPopUp((prev) => !prev);
                  }}
                >
                  <FaUserPlus />
                </div>
              ) : null}
              <div className="relative">
                <div
                  className="rounded-full hover:bg-gray-200 p-3"
                  onClick={(e) => {
                    setGroupSettings((prev) => !prev);
                    e.stopPropagation();
                  }}
                >
                  <BsThreeDotsVertical />
                </div>
                {groupSettings && (
                  <div className="absolute bg bg-white py-2 shadow border border-gray-200 rounded-lg right-0 text-gray-900 text-sm">
                    {activeChat.type === "group" && (
                      <>
                        <div
                          className="hover:bg-gray-50 hover:cursor-pointer px-2 py-1"
                          onClick={(e) => {
                            setGroupLeavePopUp(true);
                            e.stopPropagation();
                          }}
                        >
                          Leave
                        </div>
                        <div
                          className="hover:bg-gray-50 px-2 hover:cursor-pointer py-1"
                          onClick={(e) => {
                            setViewMembersPopUp(true);
                            e.stopPropagation();
                          }}
                        >
                          Members
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
          <div
            ref={listRef}
            className={`flex-1 min-h-0 overflow-y-auto p-4 flex flex-col`}
          >
            <div ref={intersectionRef} className="h-24 shrink-0"></div>
            {listOfMessagesAndInfo.messages.map((mess, index, messages) => {
              const prevMess = index > 0 ? messages[index - 1] : null;

              const sameSender =
                prevMess &&
                String(prevMess.sender?._id) === String(mess.sender?._id);

              const curDate = new Date(mess.createdAt).toLocaleDateString(
                "en-IN",
                {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                },
              );

              const prevDate = prevMess
                ? new Date(prevMess.createdAt).toLocaleDateString("en-IN", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                  })
                : null;

              const sameDate = prevMess && curDate === prevDate;
              return (
                <div key={mess._id} className="relative ">
                  {!sameDate && (
                    <div className=" my-2">
                      <div className="m-auto  text-gray-600 text-xs px-3 rounded-lg max-w-md text-center  py-2">
                        {getDateLabel(curDate)}
                      </div>
                    </div>
                  )}
                  {mess.conversationType === "group" &&
                    mess.sender &&
                    String(mess.sender._id || mess.sender) !== userId &&
                    (!sameSender || !sameDate) && (
                      <div className="font-semibold text-gray-900 ml-10">
                        {mess.sender.username}
                      </div>
                    )}
                  {index === messageHoverInfo &&
                    mess.sender &&
                    String(mess.sender._id) === String(userId) &&
                    mess.deleteStatus === "alive" && (
                      <div
                        onClick={() => {
                          setDeleteMessagePopUp(mess._id);
                        }}
                        className={`flex ${String(mess.sender._id) === String(userId) ? "justify-end" : ""}`}
                      >
                        <div
                          className={`absolute bg-white p-1 rounded-lg shadow w-13 border border-gray-400  z-30`}
                          onMouseEnter={() => setMessageHoverInfo(index)}
                          onMouseLeave={() => setMessageHoverInfo(null)}
                        >
                          delete
                        </div>
                      </div>
                    )}

                  <div
                    className={`${mess.sender && mess.conversationType === "group" && getMessageSenderId(mess) !== String(effectiveUserId) ? "flex gap-2" : ""}`}
                  >
                    {mess.conversationType === "group" &&
                      mess.sender &&
                      String(mess.sender._id || mess.sender) !== userId && (
                        <span className="relative flex size-10 shrink-0 overflow-hidden rounded-full h-8 w-8 flex-shrink-0">
                          {(!sameSender || !sameDate) && (
                            <img
                              src="https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQhxtx5uOH2FwWtC_-HAnN75PQqBFgfLF9D2w&s"
                              className="aspect-square size-full"
                            />
                          )}
                        </span>
                      )}
                    <div
                      className={` ${getMessageSenderId(mess) === String(effectiveUserId) ? "w-max ml-auto bg-blue-600 rounded-2xl rounded-tr-sm text-white px-3 py-2 mt-1" : getMessageSenderId(mess) === "" ? "m-auto  text-gray-600 text-xs px-3 rounded-lg max-w-md text-center" : "bg-gray-200 rounded-2xl rounded-tl-sm text-gray-900 px-3 py-2 mt-1 w-max"}   max-w-7/12 wrap-break-word`}
                      onMouseEnter={() => setMessageHoverInfo(index)}
                      onMouseLeave={() => setMessageHoverInfo(null)}
                    >
                      {mess.deleteStatus === "deleted" ? (
                        <div className="text-sm">This message was deleted</div>
                      ) : (
                        <>
                          {mess.messageType === "text" && (
                            <div className="text-sm">{mess.contentPreview}</div>
                          )}
                          {mess.messageType === "image" && (
                            <>
                              <img
                                src={mess?.attachment?.url}
                                alt="Uploaded image"
                                className="max-w-xs rounded-lg"
                                onClick={() =>
                                  setPreviewImageSrc(
                                    (prev) => mess.attachment.url,
                                  )
                                }
                              />
                              <p className="text-sm">{mess.contentPreview}</p>
                            </>
                          )}
                          {mess.messageType === "video" && (
                            <>
                              <video
                                src={mess.attachment.url}
                                controls
                                className="max-w-xs rounded-lg"
                              />
                              <p className="text-sm">{mess.contentPreview}</p>
                            </>
                          )}
                          {mess.messageType === "audio" && (
                            <>
                              <audio src={mess.attachment.url} controls />
                              <p className="text-sm">{mess.contentPreview}</p>
                            </>
                          )}
                          {mess.messageType === "file" && (
                            <a
                              href={mess.attachment.url}
                              target="_blank"
                              rel="noreferrer"
                              className="underline"
                            >
                              {mess.attachment.name}
                            </a>
                          )}
                        </>
                      )}
                      <div className="flex gap-1 items-center mt-1">
                        {mess.sender && (
                          <div
                            className={`text-xs ${getMessageSenderId(mess) === String(effectiveUserId) ? "text-blue-100" : "text-gray-500"} `}
                          >
                            {new Date(mess.createdAt).toLocaleTimeString([], {
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </div>
                        )}
                        {mess.sender &&
                          getMessageSenderId(mess) ===
                            String(effectiveUserId) && (
                            <div
                              className={`h-1 w-1 rounded-full bg-blue-100`}
                            ></div>
                          )}
                        <div className="text-xs text-blue-100">
                          {getMessageSenderId(mess) ===
                            String(effectiveUserId) &&
                          mess.deleteStatus === "alive" ? (
                            mess.status === "read" ? (
                              // <IoCheckmarkDoneSharp
                              //   size={15}
                              //   className="text-blue-500"
                              // />
                              "seen"
                            ) : mess.status === "delivered" ? (
                              // <IoCheckmarkDoneSharp size={15} />
                              "delivered"
                            ) : mess.status === "sent" ? (
                              // <IoCheckmark size={15} />
                              "sent"
                            ) : (
                              <IoMdTime />
                            )
                          ) : null}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <div className="shrink-0 p-2 sm:p-4 border-t border-gray-200 bg-white z-20">
          {selectedFile ? (
            <>
              {selectedFilePreviewUrl ? (
                <div className="mb-2 border border-gray-300 rounded p-2 w-fit">
                  {selectedFile?.type?.startsWith("image/") ? (
                    <img
                      src={selectedFilePreviewUrl}
                      alt="preview"
                      className="max-w-[140px] rounded cursor-pointer"
                      onClick={() => setPreviewImageSrc(selectedFilePreviewUrl)}
                    />
                  ) : selectedFile?.type?.startsWith("video/") ? (
                    <video
                      src={selectedFilePreviewUrl}
                      controls
                      className="max-w-[180px] rounded"
                    />
                  ) : null}
                </div>
              ) : (
                <div className="text-sm text-gray-900">{selectedFile.name}</div>
              )}
              <button
                className="text-xs mt-1 underline"
                onClick={() => setSelectedFile(null)}
              >
                Remove
              </button>
            </>
          ) : null}
          {(
            activeChat.type === "group"
              ? activeChat.allParticipants.includes(userId)
              : workspace.members.some(
                  (member) =>
                    String(member.userId) === String(activeChat.participantIds),
                )
          ) ? (
            <div className="flex items-end gap-2 ">
              <div className="flex-1 border border-gray-300 rounded-lg focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500">
                <input
                  type="text"
                  placeholder="Message here"
                  className="file:text-foreground placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground dark:bg-input/30 border-input flex h-9 w-full min-w-0 rounded-md px-3 py-1 text-base bg-input-background transition-[color,box-shadow] outline-none file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive border-0 focus-visible:ring-0 focus-visible:ring-offset-0"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                />
                <div className="bg-[#f3f3f5] rounded-lg flex items-center gap-1 px-2 py-1">
                  <label className="inline-flex items-center justify-center whitespace-nowrap text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50 rounded-md gap-1.5 has-[>svg]:px-2.5 h-8 w-8 p-0">
                    {/* <CiCirclePlus size={30} /> */}
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="lucide lucide-paperclip h-4 w-4"
                    >
                      <path d="M13.234 20.252 21 12.3"></path>
                      <path d="m16 6-8.414 8.586a2 2 0 0 0 0 2.828 2 2 0 0 0 2.828 0l8.414-8.586a4 4 0 0 0 0-5.656 4 4 0 0 0-5.656 0l-8.415 8.585a6 6 0 1 0 8.486 8.486"></path>
                    </svg>
                    <input
                      type="file"
                      accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.zip"
                      onChange={onSelectFile}
                      className="hidden"
                    />
                  </label>
                  <label className="inline-flex items-center justify-center whitespace-nowrap text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50 rounded-md gap-1.5 has-[>svg]:px-2.5 h-8 w-8 p-0">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="lucide lucide-smile h-4 w-4"
                    >
                      <circle cx="12" cy="12" r="10"></circle>
                      <path d="M8 14s1.5 2 4 2 4-2 4-2"></path>
                      <line x1="9" x2="9.01" y1="9" y2="9"></line>
                      <line x1="15" x2="15.01" y1="9" y2="9"></line>
                    </svg>
                  </label>
                  <label className="inline-flex items-center justify-center whitespace-nowrap text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50 rounded-md gap-1.5 has-[>svg]:px-2.5 h-8 w-8 p-0">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="lucide lucide-at-sign h-4 w-4"
                    >
                      <circle cx="12" cy="12" r="4"></circle>
                      <path d="M16 8v5a3 3 0 0 0 6 0v-1a10 10 0 1 0-4 8"></path>
                    </svg>
                  </label>
                </div>
              </div>

              <button
                aria-label="Send" title="Send message"
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive bg-[#030213] text-white hover:bg-[#030213]/90 py-2 has-[>svg]:px-3 h-10 px-4"
                onClick={() => {
                  const trimmedMessage = message.trim();
                  const hasText = trimmedMessage.length > 0;
                  const hasFile = Boolean(selectedFile);
                  if (!hasText && !hasFile) return;
                  const tempId = `temp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
                  const newMessage = {
                    _id: tempId,
                    sender: effectiveUserId,
                    receiver: [activeChat._id],
                    messageType: !selectedFile
                      ? "text"
                      : getMessageType(selectedFile),
                    contentPreview: trimmedMessage,
                    attachment: selectedFile
                      ? {
                          url: selectedFilePreviewUrl,
                          publicId: null,
                          name: selectedFile.name,
                          mime: selectedFile.type,
                          size: selectedFile.size,
                          resourceType: getMessageType(selectedFile),
                          duration: null,
                          width: null,
                          height: null,
                        }
                      : {},
                    // payload: {
                    //   content: trimmedMessage,
                    //   imageData: hasImage ? selectedImage.dataUrl : null,
                    //   imageMime: hasImage ? selectedImage.mime : null,
                    // },
                    createdAt: new Date().toISOString(),
                    deleteStatus: "alive",
                    status: "not_sent",
                    type: "normal", // or Info
                  };
                  setListOfMessagesAndInfo((prev) => {
                    if (!prev) return prev;
                    const prevMessages = Array.isArray(prev.messages)
                      ? prev.messages
                      : [];
                    const nextState = {
                      ...prev,
                      messages: [...prevMessages, newMessage],
                    };

                    return nextState;
                  });
                  setScrollToBottom((prev) => !prev);
                  setChats((prev) => {
                    if (!Array.isArray(prev)) return prev;
                    const activeId = String(activeChat?._id || "");
                    const hasExisting = prev.some(
                      (friendInfo) =>
                        String(
                          friendInfo?.friend?._id ||
                            friendInfo?.group?._id ||
                            "",
                        ) === activeId,
                    );

                    if (!hasExisting) {
                      const friendInfo = {
                        friend: {
                          _id: activeChat?._id,
                          username: activeChat?.username,
                          email: activeChat?.email,
                        },
                        lastMessage: newMessage,
                      };
                      return [friendInfo, ...prev];
                    }

                    const target = prev.find(
                      (friendInfo) =>
                        String(
                          friendInfo?.friend?._id ||
                            friendInfo?.group?._id ||
                            "",
                        ) === activeId,
                    );
                    const updatedFriend = {
                      ...target,
                      lastMessage: newMessage,
                    };

                    return [
                      updatedFriend,
                      ...prev.filter(
                        (friendInfo) =>
                          String(
                            friendInfo?.friend?._id ||
                              friendInfo?.group?._id ||
                              "",
                          ) !== activeId,
                      ),
                    ];
                  });
                  setMessage("");
                  setSelectedFile(null);
                  sendMessage(tempId, {
                    receiverId: activeChat._id,
                    name: activeChat?.username,
                    email: activeChat.email,
                    content: trimmedMessage,
                    messageType: !selectedFile
                      ? "text"
                      : getMessageType(selectedFile),

                    // imageData: hasImage ? selectedImage.dataUrl : null,
                    // imageMime: hasImage ? selectedImage.mime : null,
                    type: activeChat.type,
                    senderName: userInfo.username,
                    senderEmail: userInfo.email,
                    workspaceId: workspace._id,
                  });
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="lucide lucide-send h-4 w-4"
                >
                  <path d="M14.536 21.686a.5.5 0 0 0 .937-.024l6.5-19a.496.496 0 0 0-.635-.635l-19 6.5a.5.5 0 0 0-.024.937l7.93 3.18a2 2 0 0 1 1.112 1.11z"></path>
                  <path d="m21.854 2.147-10.94 10.939"></path>
                </svg>
              </button>
            </div>
          ) : (
            <div className="text-center text-sm text-gray-600">
              {activeChat.type === "group"
                ? "You are no longer part of this group"
                : `${activeChat.username.toUpperCase()} is no longer part of this group`}
            </div>
          )}
        </div>
        {viewMembersPopUp && (
          <ViewMembers
            setViewMembersPopUp={setViewMembersPopUp}
            activeChat={activeChat}
            setActiveChat={setActiveChat}
            activeChatRef={activeChatRef}
            emitWithAck={emitWithAck}
            setListOfMessagesAndInfo={setListOfMessagesAndInfo}
            setChats={setChats}
          />
        )}
        {groupLeavePopUp && (
          <GroupLeave
            setGroupLeavePopUp={setGroupLeavePopUp}
            emitWithAck={emitWithAck}
            setChats={setChats}
            activeChat={activeChat}
            setActiveChat={setActiveChat}
          />
        )}
        {deleteMessagePopUp && (
          <div
            className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
            onClick={() => setDeleteMessagePopUp(null)}
          >
            <div
              className="h-[30vh] w-[40vw] rounded bg-white p-5"
              onClick={(e) => e.stopPropagation()}
            >
              <div>Are you sure you want to delete?</div>
              <div className="flex gap-2 justify-end items-center">
                <button
                  onClick={() => setDeleteMessagePopUp(null)}
                  className="border border-gray-400 rounded-lg p-2"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    setDeleteMessageButtonSpinner(true);
                    deleteMessage(String(deleteMessagePopUp), activeChat.type);
                  }}
                  className="text-white bg-red-700 rounded-lg p-2 flex items-center whitespace-nowrap"
                >
                  {deleteMessageButtonSpinner && (
                    <svg
                      className="mr-3 size-5 animate-spin"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <circle
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                        className="opacity-25"
                      />
                      <path
                        d="M22 12a10 10 0 0 1-10 10"
                        stroke="currentColor"
                        strokeWidth="4"
                        className="opacity-75"
                      />
                    </svg>
                  )}
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
        {addMembersPopUp ? (
          <div
            className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
            onClick={(e) => {
              setAddMembersPopUp(false);
            }}
          >
            {!people ? (
              <h1>loading...</h1>
            ) : (
              <div
                className="w-[90dvw] md:w-[50dvw] max-h-[90vh] rounded-lg bg-white p-5"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="mb-4">
                  <div className="flex justify-between mb-2">
                    <h2 className="text-lg leading-none font-semibold flex-1">
                      Add Members
                    </h2>
                    <RxCross2 onClick={() => setAddMembersPopUp(false)} />
                  </div>
                  <p className="text-[#717182] text-sm">
                    Add people to this group
                  </p>
                </div>
                <div className="flex flex-wrap gap-2 mb-4">
                  {selectedPeopleToAddToGroup.map((member) => (
                    <div
                      key={member._id}
                      className="flex items-center text-center text-sm rounded-full pl-3 pr-1  bg-blue-200 gap-2 border border-blue-400"
                    >
                      <div>{member.username}</div>
                      <div
                        onClick={() => {
                          setCheckedValues((prev) =>
                            prev.filter((p) => p !== member._id),
                          );
                          setSelectedPeopleToAddToGroup((prev) =>
                            prev.filter((p) => p._id !== member._id),
                          );
                        }}
                      >
                        <RxCross2 size={12} />
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mb-4">
                  <input
                    value={addMembersSearch}
                    onChange={(e) => setAddMembersSearch(e.target.value)}
                    className="file:text-foreground placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground dark:bg-input/30 border-input flex h-9 w-full min-w-0 rounded-md border px-3 py-1 text-base bg-[#e2e2e2] transition-[color,box-shadow] outline-none file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive border-none"
                    placeholder="search..."
                  />
                </div>
                <div className="overflow-y-auto h-8/12">
                  {filteredPeople.map((person) => {
                    return (
                      <div
                        key={person._id}
                        className={`${activeChat.participantIds.includes(person._id) ? "pointer-events-none cursor-not-allowed" : ""} border border-gray-200 rounded-lg mt-2 p-3 hover:bg-gray-50 `}
                      >
                        <label className="flex justify-between gap-2 items-center font-medium">
                          <div className="relative">
                            <span className="relative flex size-10 shrink-0 overflow-hidden rounded-full h-10 w-10">
                              <img
                                className="aspect-square size-full"
                                src="https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQhxtx5uOH2FwWtC_-HAnN75PQqBFgfLF9D2w&s"
                              ></img>
                            </span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div>
                              {person.username}
                              {
                                <span className="font-normal text-sm">
                                  {activeChat.participantIds.includes(
                                    person._id,
                                  )
                                    ? " (Already a member)"
                                    : null}
                                </span>
                              }
                            </div>
                            {/* <div>{person.email}</div> */}
                          </div>
                          <input
                            type="checkbox"
                            checked={checkedValues.includes(person._id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setCheckedValues((prev) => [
                                  ...prev,
                                  person._id,
                                ]);
                                setSelectedPeopleToAddToGroup((prev) => [
                                  ...prev,
                                  person,
                                ]);
                              } else {
                                setCheckedValues((prev) =>
                                  prev.filter((p) => p !== person._id),
                                );
                                setSelectedPeopleToAddToGroup((prev) =>
                                  prev.filter((p) => p._id !== person._id),
                                );
                              }
                            }}
                          />
                        </label>
                      </div>
                    );
                  })}
                </div>
                <div>
                  <button
                    className={`p-2 w-full bg-black/90 text-white font-bold rounded-lg mt-6 ${selectedPeopleToAddToGroup.length === 0 ? "cursor-not-allowed" : "cursor-pointer"}`}
                    onClick={addMembersToGroup}
                    disabled={selectedPeopleToAddToGroup.length === 0}
                  >
                    Add
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : null}
        {previewImageSrc ? (
          <div
            className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
            onClick={() => setPreviewImageSrc(null)}
          >
            <img
              src={previewImageSrc}
              alt="image preview"
              className="max-h-[90vh] max-w-[90vw] rounded"
              onClick={(e) => e.stopPropagation()}
            />
            <button
              className="absolute top-4 right-4 text-white border border-white rounded px-3 py-1"
              onClick={() => setPreviewImageSrc(null)}
            >
              Close
            </button>
          </div>
        ) : null}
      </div>
    )
  ) : (
    <p className="hidden md:block w-6/12  m-auto text-center text-gray-500">
      please select a chat to start messaging
    </p>
  );
};

export default ChatSpace;
